import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  X,
  Package,
  Truck,
  Banknote,
  RotateCcw,
  Users,
  AlertTriangle,
  ShoppingBag,
  Receipt,
  Clock,
  CornerDownLeft,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { useGlobalSearch } from '@/hooksApi/useGlobalSearch';
import type { GlobalSearchResult, SearchCategory } from '@/types/globalSearch';

/* ─── Constants ─── */
const RECENT_KEY = 'boxy-recent-searches';
const MAX_RECENT = 5;
const SEARCH_DEBOUNCE_MS = 300;

/* ─── Recent searches helpers ─── */
function getRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]').slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function addRecent(q: string): void {
  const trimmed = q.trim();
  if (!trimmed) return;
  const list = getRecent().filter((r) => r !== trimmed);
  list.unshift(trimmed);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
}

function removeRecent(q: string): void {
  localStorage.setItem(RECENT_KEY, JSON.stringify(getRecent().filter((r) => r !== q)));
}

function clearRecent(): void {
  localStorage.removeItem(RECENT_KEY);
}

/* ─── Category icon + label mapping ─── */
const CATEGORY_META: Record<
  SearchCategory,
  { icon: typeof Package; labelKey: string; viewAllHref: (q: string) => string }
> = {
  order: {
    icon: Package,
    labelKey: 'orders',
    viewAllHref: (q) => `/orders?search=${encodeURIComponent(q)}`,
  },
  pickup: {
    icon: Truck,
    labelKey: 'pickups',
    viewAllHref: (q) => `/pickups?search=${encodeURIComponent(q)}`,
  },
  remittance: {
    icon: Banknote,
    labelKey: 'remittances',
    viewAllHref: () => `/finance?tab=cod_remittances`,
  },
  rto: {
    icon: RotateCcw,
    labelKey: 'rto',
    viewAllHref: (q) => `/pickups?tab=return_to_origin&search=${encodeURIComponent(q)}`,
  },
  contact: {
    icon: Users,
    labelKey: 'contacts',
    viewAllHref: () => `/settings/address-book`,
  },
  claim: {
    icon: AlertTriangle,
    labelKey: 'claims',
    viewAllHref: () => `/claims`,
  },
  product: {
    icon: ShoppingBag,
    labelKey: 'product_catalog',
    viewAllHref: () => `/settings/product-catalog`,
  },
  transaction: {
    icon: Receipt,
    labelKey: 'transactions',
    viewAllHref: () => `/finance?tab=transactions`,
  },
};

/* ─── Status badge styles ─── */
const STATUS_BADGE_STYLES: Record<string, { bg: string; text: string }> = {
  new: { bg: 'hsla(268,60%,60%,0.12)', text: 'hsl(268,55%,52%)' },
  scheduled: { bg: 'hsla(210,70%,55%,0.12)', text: 'hsl(210,65%,45%)' },
  collecting: { bg: 'hsla(30,75%,55%,0.12)', text: 'hsl(30,70%,42%)' },
  'out-for-collecting': { bg: 'hsla(30,70%,55%,0.12)', text: 'hsl(30,65%,42%)' },
  collected: { bg: 'hsla(95,55%,55%,0.13)', text: 'hsl(95,50%,40%)' },
  sorted: { bg: 'hsla(200,60%,50%,0.12)', text: 'hsl(200,55%,40%)' },
  'transferred-carrier': { bg: 'hsla(195,70%,50%,0.12)', text: 'hsl(195,65%,40%)' },
  'in-transit': { bg: 'hsla(195,70%,50%,0.12)', text: 'hsl(195,65%,40%)' },
  'out-for-delivery': { bg: 'hsla(145,60%,48%,0.12)', text: 'hsl(145,55%,38%)' },
  postponed: { bg: 'hsla(40,90%,55%,0.12)', text: 'hsl(40,80%,40%)' },
  'on-hold': { bg: 'hsla(35,85%,52%,0.12)', text: 'hsl(35,75%,38%)' },
  'hold-resolved': { bg: 'hsla(35,80%,52%,0.12)', text: 'hsl(35,70%,38%)' },
  'retry-request': { bg: 'hsla(35,80%,52%,0.12)', text: 'hsl(35,70%,38%)' },
  exception: { bg: 'hsla(0,80%,55%,0.12)', text: 'hsl(0,70%,45%)' },
  delivered: { bg: 'hsla(152,60%,48%,0.12)', text: 'hsl(152,60%,38%)' },
  'partially-delivered': { bg: 'hsla(140,50%,48%,0.12)', text: 'hsl(140,45%,38%)' },
  'rto-delivered': { bg: 'hsla(340,50%,52%,0.12)', text: 'hsl(340,45%,42%)' },
  'rto-partially-delivered': { bg: 'hsla(330,45%,52%,0.12)', text: 'hsl(330,40%,42%)' },
  'rto-requested': { bg: 'hsla(15,70%,55%,0.12)', text: 'hsl(15,65%,42%)' },
  'rto-request-pending': { bg: 'hsla(5,65%,52%,0.12)', text: 'hsl(5,60%,42%)' },
  'rto-in-transit': { bg: 'hsla(10,65%,52%,0.12)', text: 'hsl(10,60%,42%)' },
  'rto-drop-off-scanned': { bg: 'hsla(10,65%,52%,0.12)', text: 'hsl(10,60%,42%)' },
  'rto-scheduled': { bg: 'hsla(355,55%,50%,0.12)', text: 'hsl(355,50%,40%)' },
  'rto-received-warehouse': { bg: 'hsla(355,55%,50%,0.12)', text: 'hsl(355,50%,40%)' },
  'rto-received-carrier-warehouse': { bg: 'hsla(355,50%,50%,0.12)', text: 'hsl(355,45%,40%)' },
  'rto-collecting': { bg: 'hsla(15,65%,52%,0.12)', text: 'hsl(15,60%,42%)' },
  'received-warehouse': { bg: 'hsla(200,55%,50%,0.12)', text: 'hsl(200,50%,40%)' },
  'received-carrier-warehouse': { bg: 'hsla(205,55%,50%,0.12)', text: 'hsl(205,50%,40%)' },
  'out-for-collection': { bg: 'hsla(30,72%,55%,0.12)', text: 'hsl(30,67%,42%)' },
  resend: { bg: 'hsla(250,50%,55%,0.12)', text: 'hsl(250,45%,45%)' },
  inventory: { bg: 'hsla(200,60%,50%,0.12)', text: 'hsl(200,55%,40%)' },
  // Generic statuses for non-order modules
  pending: { bg: 'hsla(40,90%,55%,0.12)', text: 'hsl(40,80%,40%)' },
  approved: { bg: 'hsla(152,60%,48%,0.12)', text: 'hsl(152,60%,38%)' },
  rejected: { bg: 'hsla(0,80%,55%,0.12)', text: 'hsl(0,70%,45%)' },
  'under-review': { bg: 'hsla(210,70%,55%,0.12)', text: 'hsl(210,65%,45%)' },
  initiated: { bg: 'hsla(268,60%,60%,0.12)', text: 'hsl(268,55%,52%)' },
};

function getStatusStyle(slug?: string): { bg: string; text: string } | undefined {
  if (!slug) return undefined;
  return (
    STATUS_BADGE_STYLES[slug] ?? { bg: 'hsla(220,10%,60%,0.12)', text: 'hsl(220,10%,45%)' }
  );
}

/* ─── Highlight matched text ─── */
function Highlight({ text, query }: { text: string; query: string }) {
  const trimmedQuery = query.trim();
  if (!trimmedQuery || !text) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(trimmedQuery.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className='bg-[#EBDD4C]/30 rounded-sm px-0.5'>
        {text.slice(idx, idx + trimmedQuery.length)}
      </span>
      {text.slice(idx + trimmedQuery.length)}
    </>
  );
}

/* ─── Main Component ─── */
export function GlobalSearch() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [recent, setRecent] = useState<string[]>(getRecent);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isRTL = i18n.language === 'ar' || i18n.dir() === 'rtl';

  /* ─── Debounce query ─── */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
      setActiveIdx(-1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  /* ─── API search — all modules in parallel ─── */
  const { groups, isLoading, totalResults } = useGlobalSearch(debouncedQuery);

  /* ─── Flatten all results for keyboard navigation ─── */
  const allItems: GlobalSearchResult[] = useMemo(
    () => groups.flatMap((g) => g.results),
    [groups],
  );

  /* ─── Dropdown visibility ─── */
  const showRecent = open && !query.trim() && recent.length > 0;
  const showResults = open && !!debouncedQuery.trim() && totalResults > 0 && !isLoading;
  const showEmpty =
    open && debouncedQuery.trim().length >= 2 && !isLoading && totalResults === 0;
  const showLoading = open && !!query.trim() && isLoading;
  const showDropdown = showRecent || showResults || showEmpty || showLoading;

  /* ─── ⌘K global shortcut ─── */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  /* ─── Click outside to close ─── */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ─── Navigation helpers ─── */
  const navigateTo = useCallback(
    (href: string) => {
      addRecent(query);
      setRecent(getRecent());
      setOpen(false);
      setQuery('');
      setDebouncedQuery('');
      navigate(href);
    },
    [navigate, query],
  );

  /* ─── Keyboard navigation ─── */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((prev) => Math.min(prev + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && allItems[activeIdx]) {
        navigateTo(allItems[activeIdx].href);
      } else if (query.trim()) {
        if (allItems.length === 1) {
          // Exactly 1 result → open it directly
          navigateTo(allItems[0].href);
        } else {
          // Multiple or zero results → go to search results page
          navigateTo(`/search?q=${encodeURIComponent(query.trim())}`);
        }
      }
    } else if (e.key === 'Escape') {
      if (query) {
        setQuery('');
        setDebouncedQuery('');
      } else {
        inputRef.current?.blur();
        setOpen(false);
      }
    }
  };

  /* ─── Compute global index offset for each group ─── */
  const groupOffsets = useMemo(() => {
    const offsets: number[] = [];
    let offset = 0;
    for (const g of groups) {
      offsets.push(offset);
      offset += g.results.length;
    }
    return offsets;
  }, [groups]);

  return (
    <div ref={containerRef} className='relative w-full max-w-[480px]' dir={isRTL ? 'rtl' : 'ltr'}>
      {/* ─── Search Input ─── */}
      <div
        className={`flex h-10 items-center gap-2.5 rounded-xl border bg-sd-card px-3.5 text-[13px] transition-colors ${
          open
            ? 'border-sd-border'
            : 'border-sd-border hover:border-sd-muted-foreground/30'
        }`}
      >
        <Search className='h-4 w-4 shrink-0 text-sd-muted-foreground' />
        <input
          ref={inputRef}
          type='text'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={String(t('search_placeholder'))}
          className='flex-1 bg-transparent text-[13px] text-sd-foreground outline-none !border-0 !border-none !shadow-none !ring-0 !p-0 placeholder:text-sd-muted-foreground'
          aria-label={String(t('search_placeholder'))}
          aria-expanded={open}
          aria-haspopup='listbox'
          role='combobox'
          aria-autocomplete='list'
        />
        {query ? (
          <button
            onClick={() => {
              setQuery('');
              setDebouncedQuery('');
              inputRef.current?.focus();
            }}
            className='flex h-5 w-5 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-sd-muted'
            aria-label='Clear search'
          >
            <X className='h-3.5 w-3.5 text-sd-muted-foreground' />
          </button>
        ) : (
          <kbd className='hidden shrink-0 items-center gap-0.5 rounded-md border border-sd-border bg-sd-muted px-1.5 py-0.5 text-[11px] font-medium text-sd-muted-foreground sm:inline-flex'>
            ⌘K
          </kbd>
        )}
      </div>

      {/* ─── Dropdown ─── */}
      {showDropdown && (
        <div
          className='absolute top-full z-50 mt-2 w-full overflow-hidden rounded-2xl border border-sd-border bg-sd-card shadow-lg'
          role='listbox'
        >
          <div className='max-h-[460px] overflow-y-auto'>
            {/* Recent Searches */}
            {showRecent && (
              <div>
                <div className='flex items-center justify-between px-4 pb-1.5 pt-3'>
                  <span className='text-[11px] font-bold uppercase tracking-wider text-sd-muted-foreground'>
                    {String(t('recent_searches'))}
                  </span>
                  <button
                    onClick={() => {
                      clearRecent();
                      setRecent([]);
                    }}
                    className='text-[11px] font-medium text-sd-muted-foreground transition-colors hover:text-sd-foreground'
                  >
                    {String(t('clear_all'))}
                  </button>
                </div>
                {recent.map((r) => (
                  <button
                    key={r}
                    onClick={() => {
                      setQuery(r);
                      inputRef.current?.focus();
                    }}
                    className='flex w-full items-center gap-3 px-4 py-2.5 text-start transition-colors hover:bg-sd-muted/50'
                  >
                    <Clock className='h-4 w-4 shrink-0 text-sd-muted-foreground' />
                    <span className='flex-1 truncate text-[13px] text-sd-foreground'>{r}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeRecent(r);
                        setRecent(getRecent());
                      }}
                      className='flex h-5 w-5 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-sd-muted'
                      aria-label={`Remove ${r} from recent searches`}
                    >
                      <X className='h-3 w-3 text-sd-muted-foreground' />
                    </button>
                  </button>
                ))}
              </div>
            )}

            {/* Loading state */}
            {showLoading && (
              <div className='px-4 py-6 text-center'>
                <div className='mx-auto h-5 w-5 animate-spin rounded-full border-2 border-sd-border border-t-sd-primary' />
                <p className='mt-2 text-[11px] text-sd-muted-foreground'>
                  {String(t('searching_all_modules'))}
                </p>
              </div>
            )}

            {/* ─── Categorized Results ─── */}
            {showResults &&
              groups.map((group, groupIdx) => {
                const meta = CATEGORY_META[group.category];
                const Icon = meta.icon;
                const baseIdx = groupOffsets[groupIdx];

                return (
                  <div key={group.category}>
                    {/* Category header */}
                    <div className='flex items-center justify-between px-4 pb-1.5 pt-3'>
                      <span className='text-[11px] font-bold uppercase tracking-wider text-sd-muted-foreground'>
                        {String(t(meta.labelKey))}
                      </span>
                      {group.total > group.results.length && (
                        <button
                          onClick={() => navigateTo(meta.viewAllHref(debouncedQuery))}
                          className='text-[11px] font-medium text-sd-muted-foreground transition-colors hover:text-sd-foreground'
                        >
                          {String(t('view_all_results'))} ({group.total})
                        </button>
                      )}
                    </div>

                    {/* Results */}
                    {group.results.map((result, idx) => {
                      const globalIdx = baseIdx + idx;
                      const isActive = activeIdx === globalIdx;
                      const statusStyle = getStatusStyle(result.statusSlug);
                      const isExact = result.isExactMatch;

                      return (
                        <button
                          key={`${result.category}-${result.id}`}
                          onClick={() => navigateTo(result.href)}
                          onMouseEnter={() => setActiveIdx(globalIdx)}
                          className={`flex w-full items-center gap-3 px-4 py-2.5 text-start transition-colors ${
                            isExact
                              ? 'bg-[#EBDD4C]/10 border-s-2 border-s-[#EBDD4C]'
                              : ''
                          } ${isActive ? 'bg-sd-muted/50' : !isExact ? 'hover:bg-sd-muted/30' : 'hover:bg-[#EBDD4C]/15'}`}
                          role='option'
                          aria-selected={isActive}
                        >
                          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isExact ? 'bg-[#EBDD4C]/20' : 'bg-sd-muted'}`}>
                            <Icon className={`h-4 w-4 ${isExact ? 'text-[#8B7D00]' : 'text-sd-muted-foreground'}`} />
                          </div>
                          <div className='min-w-0 flex-1'>
                            <div className='flex items-center gap-2'>
                              <p className='truncate text-[13px] font-semibold text-sd-foreground'>
                                <Highlight text={result.primary} query={debouncedQuery} />
                              </p>
                              {result.tag && (
                                // FE-10: long custom IDs must truncate — not push the row wider.
                                <span
                                  title={result.tag}
                                  className={`max-w-[180px] truncate rounded-md px-1.5 py-0.5 text-[11px] font-bold ${
                                  isExact
                                    ? 'bg-[#EBDD4C]/30 text-[#6B5F00]'
                                    : 'bg-sd-muted text-sd-foreground'
                                }`}>
                                  <Highlight text={result.tag} query={debouncedQuery} />
                                </span>
                              )}
                              {isExact && (
                                <span className='shrink-0 text-[10px] font-semibold text-[#8B7D00] uppercase tracking-wide'>
                                  {String(t('exact_match'))}
                                </span>
                              )}
                            </div>
                            {result.secondary && (
                              <p className='mt-0.5 truncate text-[11px] text-sd-muted-foreground'>
                                <Highlight text={result.secondary} query={debouncedQuery} />
                              </p>
                            )}
                          </div>
                          {result.statusText && statusStyle && (
                            <span
                              className='shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold'
                              style={{
                                background: statusStyle.bg,
                                color: statusStyle.text,
                              }}
                            >
                              {result.category === 'order'
                                ? String(
                                    t(
                                      `merchant_title_order_status.${result.statusText}`,
                                      result.statusText,
                                    ),
                                  )
                                : result.statusText}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}

            {/* Empty State */}
            {showEmpty && (
              <div className='px-4 py-8 text-center'>
                <Search className='mx-auto mb-2.5 h-8 w-8 opacity-40 text-sd-muted-foreground' />
                <p className='text-[13px] font-semibold text-sd-foreground'>
                  {String(t('no_results_found'))}
                </p>
                <p className='mt-1 text-[11px] text-sd-muted-foreground'>
                  {String(t('no_results_found_hint'))}
                </p>
              </div>
            )}
          </div>

          {/* ─── View all results footer ─── */}
          {totalResults > 0 && debouncedQuery && (
            <button
              onClick={() => navigateTo(`/search?q=${encodeURIComponent(debouncedQuery)}`)}
              className='flex items-center justify-between w-full px-4 py-2.5 border-t border-sd-border bg-transparent transition-colors hover:bg-sd-muted/50 cursor-pointer text-start'
            >
              <span className='text-[13px] font-medium text-sd-foreground'>
                {String(t('view_all_results'))} ({totalResults})
              </span>
              <CornerDownLeft className='h-3.5 w-3.5 text-sd-muted-foreground' />
            </button>
          )}

          {/* ─── Keyboard hint footer ─── */}
          <div className='flex items-center gap-4 border-t border-sd-border px-4 py-2'>
            <div className='flex items-center gap-1.5'>
              <kbd className='inline-flex h-5 w-5 items-center justify-center rounded border border-sd-border bg-sd-muted text-[10px] text-sd-muted-foreground'>
                <ArrowUp className='h-3 w-3' />
              </kbd>
              <kbd className='inline-flex h-5 w-5 items-center justify-center rounded border border-sd-border bg-sd-muted text-[10px] text-sd-muted-foreground'>
                <ArrowDown className='h-3 w-3' />
              </kbd>
              <span className='text-[11px] text-sd-muted-foreground'>{String(t('navigate'))}</span>
            </div>
            <div className='flex items-center gap-1.5'>
              <kbd className='inline-flex h-5 w-5 items-center justify-center rounded border border-sd-border bg-sd-muted text-[10px] text-sd-muted-foreground'>
                <CornerDownLeft className='h-3 w-3' />
              </kbd>
              <span className='text-[11px] text-sd-muted-foreground'>{String(t('open'))}</span>
            </div>
            <div className='flex items-center gap-1.5'>
              <kbd className='inline-flex h-5 items-center justify-center rounded border border-sd-border bg-sd-muted px-1 font-mono text-[10px] text-sd-muted-foreground'>
                esc
              </kbd>
              <span className='text-[11px] text-sd-muted-foreground'>{String(t('close'))}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
